// Session 27: the learner's personal prompt library (/courses/prompts).
//
// The thing they keep after the course ends, which is why it is its own page
// rather than a panel inside a lesson. Prompts arrive from a lesson's prompt
// card, and a learner can add their own.
//
// v_portal_course_prompt_cards is scoped to auth.uid() in the view itself, so
// this page cannot show anyone else's prompts even by mistake.

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabase.js';
import { useAuth } from '../AuthContext.jsx';
import { useClientPath } from '../lib/clientLink.js';
import Placeholder from './Placeholder.jsx';
import { courseMode } from '../modules/index.js';
import { COURSES, courseByKey } from '../courses/index.js';
import { SaveStatus, useSaveStatus } from '../components/SaveStatus.jsx';

export default function CoursePrompts() {
  const { entitlements } = useAuth();
  const mode = courseMode(entitlements);
  if (mode !== 'on') return <Placeholder />;
  return <PromptLibrary />;
}

function PromptLibrary() {
  const to = useClientPath();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const load = useCallback(async () => {
    const { data, error: e } = await supabase
      .from('v_portal_course_prompt_cards').select('*').order('created_at', { ascending: false });
    if (e) { setError(e.message); setLoading(false); return; }
    setCards(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function copy(card) {
    try {
      await navigator.clipboard.writeText(card.body);
      setCopiedId(card.id);
      setTimeout(() => setCopiedId(null), 2500);
    } catch { /* clipboard can be blocked; the text is selectable */ }
  }

  async function remove(id) {
    const { error: e } = await supabase.rpc('portal_course_delete_prompt', { p_id: id });
    if (e) { setError(e.message); return; }
    load();
  }

  return (
    <>
      <h1>My prompt library</h1>
      <p className="lede">Prompts you saved from a lesson, plus any you wrote yourself.</p>
      {error && <p className="banner banner-err">{error}</p>}

      <AddPrompt onSaved={load} />

      {loading ? (
        <p className="lede">Loading…</p>
      ) : cards.length === 0 ? (
        <div className="empty">
          <h2>Nothing saved yet</h2>
          <p>
            Every lesson ends with a prompt card you can keep.{' '}
            {COURSES.length === 1
              ? <Link to={to(`/courses/${COURSES[0].key}`)}>Start {COURSES[0].title}</Link>
              : 'Open a course to get started.'}
          </p>
        </div>
      ) : (
        <div className="course-prompts">
          {cards.map((c) => (
            <div key={c.id} className="card course-prompt">
              <p className="course-prompt-title">{c.title}</p>
              <blockquote className="course-prompt-body">{c.body}</blockquote>
              <p className="course-muted">
                {c.source === 'own' ? 'Your own' : fromLesson(c)}
              </p>
              <div className="course-prompt-actions">
                <button type="button" className="btn" onClick={() => copy(c)}>
                  {copiedId === c.id ? 'Copied' : 'Copy'}
                </button>
                <button type="button" className="btn btn-danger" onClick={() => remove(c.id)}>Remove</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function fromLesson(card) {
  const course = courseByKey(card.course_key);
  const lesson = course?.lessons.find((l) => l.key === card.lesson_key);
  if (!course) return 'From a lesson';
  return lesson ? `From ${course.title}: ${lesson.title}` : `From ${course.title}`;
}

function AddPrompt({ onSaved }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const save = useSaveStatus();

  async function submit(e) {
    e.preventDefault();
    save.start();
    const { error } = await supabase.rpc('portal_course_save_prompt', {
      p_course_key: 'own', p_lesson_key: null,
      p_title: title, p_body: body, p_source: 'own',
    });
    if (error) return save.fail(error.message);
    setTitle(''); setBody(''); setOpen(false);
    save.ok();
    if (onSaved) onSaved();
  }

  if (!open) {
    return (
      <p className="course-block">
        <button type="button" className="btn" onClick={() => setOpen(true)}>Add your own prompt</button>
      </p>
    );
  }

  return (
    <section className="course-block card">
      <form onSubmit={submit}>
        <label htmlFor="own-prompt-title">What is it for?</label>
        <input id="own-prompt-title" className="course-input" type="text" value={title}
               onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Reply to a pricing question" />
        <label htmlFor="own-prompt-body">The prompt</label>
        <textarea id="own-prompt-body" className="course-textarea" rows={4} value={body}
                  onChange={(e) => setBody(e.target.value)} />
        <div className="course-prompt-actions">
          <button type="submit" className="btn btn-primary" disabled={!title.trim() || !body.trim()}>Save</button>
          <button type="button" className="btn" onClick={() => setOpen(false)}>Cancel</button>
          <SaveStatus status={save.status} />
        </div>
      </form>
    </section>
  );
}
